<template>
  <div id="login">
    <div class="main-box">
      <div class="title">登录</div>
      
      <!-- 工号输入框 -->
      <div class="user-code">
        <el-input 
          v-model="userCode" 
          placeholder="请输入工号"
          maxlength="20"
          @blur="validateUserCode"
        ></el-input>
      </div>
      
      <!-- 用户名输入框 -->
      <div class="username">
        <el-input 
          v-model="username" 
          placeholder="请输入用户名"
          maxlength="50"
          @blur="validateUsername"
        ></el-input>
      </div>
      
      <!-- 密码输入框 -->
      <div class="password">
        <el-input
          placeholder="请输入密码"
          v-model="password"
          show-password
          maxlength="100"
          @blur="validatePassword"
        ></el-input>
      </div>
      
      <!-- 环境状态提示：部署中 / 断网 / 资源加载失败（常驻横幅，比一闪而过的 message 更容易被看到） -->
      <div class="login-status" v-if="envAlert">
        <el-alert
          :title="envAlert"
          type="error"
          :closable="false"
          center
          style="width: 250px;"
        ></el-alert>
      </div>

      <!-- 初始化阶段状态提示（getPublicKey） -->
      <div class="login-status" v-if="encryptStatus">
        <el-alert
          :title="encryptStatus"
          :type="encryptStatusType"
          :closable="false"
          center
          style="width: 250px;"
        ></el-alert>
      </div>

      <!-- 登录阶段状态提示（userLogin） -->
      <div class="login-status" v-if="loginStatus">
        <el-alert
          :title="loginStatus"
          :type="loginStatusType"
          :closable="false"
          center
          style="width: 250px;"
        ></el-alert>
      </div>
      
      <div class="bottom">
        <el-checkbox label="记住密码" v-model="savePassword"></el-checkbox>
      </div>

      <div class="bottom">
        <el-button type="primary" :loading="loading" @click="login">登录</el-button>
        <el-button type="primary" :loading="registerLoading" @click="register">注册</el-button>
      </div>

    </div>
  </div>
</template>

<script>
import { userLogin, getPublicKey, userRegister } from '@/api/mainapi.js';
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';
import RSAEncryptionUtils from '@/utils/rsaEncryptionUtils.js';
import UserInfoUtils from '@/utils/userInfoUtils.js';

export default {
  name: 'login',
  data() {
    return {
      savePassword: false,
      userCode: '',      // 工号
      username: '',      // 用户名
      password: '',      // 密码
      loading: false,
      registerLoading: false, // 注册按钮的独立loading状态
        
      // 加密相关状态
      encryptStatus: '',
      encryptStatusType: 'info',
      rsaPublicKey: '',
      keyId: '',
      isKeyLoaded: false, // 标记密钥是否已加载

      // 登录阶段状态提示
      loginStatus: '',
      loginStatusType: 'info',

      // ✅ 环境类异常（部署中 / 断网 / 静态资源加载失败）：常驻提示
      networkOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
      deployHint: '',
      healthTimer: null,

      // 定时器句柄
      rsaInitTimer: null,
      loginHintTimer: null,
      loginHintTimer2: null,
      loginTimeoutTimer: null,
    };
  },
  computed: {
    /** ✅ 环境提示：断网 > 部署 / 资源加载失败
     *  ⚠️ 密钥阶段 / 登录阶段已经在显示提示时这里让位，保证同一时刻只有一条提示 */
    envAlert() {
      if (this.encryptStatus || this.loginStatus) return '';
      if (!this.networkOnline) return '网络已断开，请检查网络连接';
      return this.deployHint;
    }
  },
  mounted() {
    // 从Cookie中获取保存的用户名和工号
    const savedUserCode = Cookies.get('userCode') ? Cookies.get('userCode') : '';
    const savedUserName = Cookies.get('userName') ? Cookies.get('userName') : '';
    
    this.userCode = savedUserCode;
    this.username = savedUserName;

    // 获取保存的密码
    const enPassword = Cookies.get('userPwd') ? Cookies.get('userPwd') : '';
    if (enPassword) {
      this.password = CryptoJS.AES.decrypt(enPassword, 'foodparty').toString(CryptoJS.enc.Utf8);
      this.savePassword = true;
    } else {
      this.password = '';
    }
    
    // 移除预加载RSA公钥的逻辑
    // this.encryptStatus = '点击登录或注册按钮开始加密流程';

    // ✅ 环境监听：断网 / 恢复、静态资源加载失败、懒加载 chunk 失败
    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);
    window.addEventListener('error', this.onResourceError, true);
    window.addEventListener('unhandledrejection', this.onChunkError);

    // ✅ 心跳探测：部署过程中旧文件被删时，页面还能报出来
    this.startHealthCheck();
  },
  beforeDestroy() {
    this.clearLoginTimers();
    if (this.rsaInitTimer) { clearTimeout(this.rsaInitTimer); this.rsaInitTimer = null; }

    this.stopHealthCheck();
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
    window.removeEventListener('error', this.onResourceError, true);
    window.removeEventListener('unhandledrejection', this.onChunkError);
  },
  methods: {
    /* ================= 环境检测：部署 / 断网 / 资源加载失败 ================= */

    /** 断网 / 恢复 */
    onOffline() {
      this.networkOnline = false;
    },
    onOnline() {
      this.networkOnline = true;
      this.deployHint = '';   // 网络恢复后清掉「连不上服务器」那类提示
    },

    /** <script> / <link> 加载失败（部署时旧文件被删 → 404） */
    onResourceError(event) {

      const msg = (event && event.message) || '';
      if (/Loading chunk|ChunkLoadError|Loading CSS chunk/i.test(msg)) {
        this.deployHint = '系统已更新，请刷新页面（Ctrl + F5）后重新登录';
      }
    },

    /** 路由懒加载 / 动态 import 失败也会走这里 */
    onChunkError(event) {
      const reason = event && event.reason;
      const name = reason && reason.name;
      const msg = (reason && reason.message) || '';
      if (name === 'ChunkLoadError' || /Loading chunk|ChunkLoadError/i.test(msg)) {
        this.deployHint = '系统已更新，请刷新页面（Ctrl + F5）后重新登录';
      }
    },

    /** ✅ 判断响应是不是 HTML：部署时 nginx 会把接口请求兜底成 404/502 的 HTML 页，
     *  而不是正常的 JSON → 这是「正在部署」最典型的信号 */
    isHtmlResponse(res) {
      if (!res) return false;
      const headers = res.headers || {};
      const ct = headers['content-type'] || headers['Content-Type'] || '';
      if (typeof ct === 'string' && ct.indexOf('text/html') !== -1) return true;
      const data = res.data;
      return typeof data === 'string' && data.trim().charAt(0) === '<';
    },

    /** 把错误归类：offline / deploying / timeout / network / server / http / unknown */
    classifyError(error) {
      if (!this.networkOnline || (typeof navigator !== 'undefined' && !navigator.onLine)) return 'offline';
      if (!error) return 'unknown';
      if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ECONNABORTED') return 'timeout';
      if (this.isHtmlResponse(error.response)) return 'deploying';
      if (error.response) return error.response.status >= 500 ? 'server' : 'http';
      if (error.code === 'ERR_NETWORK' || /Network Error|Failed to fetch/i.test(error.message || '')) return 'network';
      // axios 把 HTML 当 JSON 解析时会抛这类错
      if (/Unexpected token|Unexpected end of JSON|in JSON/i.test(error.message || '')) return 'deploying';
      return 'unknown';
    },

    /** 各类型对应的提示文案 */
    errorText(type) {
      switch (type) {
        case 'offline':   return '网络已断开，请检查网络连接';
        case 'deploying': return '系统正在部署更新，请稍后刷新页面重试';
        case 'timeout':   return '请求超时，服务器可能正在部署或网络较慢，请稍后重试';
        case 'network':   return '无法连接服务器，请检查网络或确认服务是否已启动';
        case 'server':    return '服务器异常（5xx），请稍后重试';
        default:          return '请求失败，请稍后重试';
      }
    },

    /** ✅ 心跳：页面开着的时候定期探一下，部署 / 服务挂了能及时提示 */
    startHealthCheck() {
      this.stopHealthCheck();
      this.healthTimer = setInterval(async () => {
        if (document.hidden) return;                 // 页面在后台就不打扰
        if (typeof navigator !== 'undefined' && !navigator.onLine) return; // 断网交给 offline 事件提示

        try {
          const res = await getPublicKey();
          if (this.isHtmlResponse(res)) {
            this.deployHint = this.errorText('deploying');
          } else if (res && res.data && res.data.code === 200) {
            this.deployHint = '';                    // 服务正常，清掉提示
          }
        } catch (error) {
          const type = this.classifyError(error);
          if (type === 'deploying' || type === 'network') {
            this.deployHint = this.errorText(type);
          }
        }
      }, 30000);   // 30s 一次，觉得频繁可以改成 60000
    },
    stopHealthCheck() {
      if (this.healthTimer) { clearInterval(this.healthTimer); this.healthTimer = null; }
    },

    /**
     * 清理登录相关定时器
     */
    clearLoginTimers() {
      if (this.loginHintTimer) { clearTimeout(this.loginHintTimer); this.loginHintTimer = null; }
      if (this.loginHintTimer2) { clearTimeout(this.loginHintTimer2); this.loginHintTimer2 = null; }
      if (this.loginTimeoutTimer) { clearTimeout(this.loginTimeoutTimer); this.loginTimeoutTimer = null; }
    },
    /**
     * 获取RSA公钥（点击按钮时调用）
     */
    async loadRsaKey() {
      this.encryptStatusType = 'info';
      this.encryptStatus = '';

      // 清除之前的定时器
      if (this.rsaInitTimer) { clearTimeout(this.rsaInitTimer); this.rsaInitTimer = null; }

      // 创建超时控制器，8秒超时
      const timeoutController = new AbortController();
      const timeoutId = setTimeout(() => {
        timeoutController.abort();
      }, 8000);

      // 5秒后提示"系统初始化中..."
      this.rsaInitTimer = setTimeout(() => {
        this.encryptStatus = '系统初始化中...';
        this.encryptStatusType = 'info';
      }, 5000);

      try {
        const res1 = await getPublicKey({ signal: timeoutController.signal });
        const res = res1.data;

        if (res.code === 200) {
          const publicKeyInfo = res.data;
          this.keyId = publicKeyInfo.keyId;
          this.rsaPublicKey = publicKeyInfo.publicKey;

          const isValid = await RSAEncryptionUtils.validatePublicKey(this.rsaPublicKey);

          if (!isValid) {
            throw new Error('公钥格式无效');
          }

          this.isKeyLoaded = true;
          this.encryptStatus = '';
          this.encryptStatusType = 'success';
          return true;
        }
        else {
          throw new Error(res.message || '获取公钥失败');
        }
      } catch (error) {
        // ✅ 按类型给文案：部署中 / 断网 / 连不上服务器 / 超时 ...
        // ⚠️ 这里只写 encryptStatus，不要再写 deployHint / $message，
        //    否则同一件事会同时出现两条提示
        const type = this.classifyError(error);
        this.encryptStatus = this.errorText(type);
        this.encryptStatusType = 'error';
        throw error;
      } finally {
        clearTimeout(timeoutId);
        if (this.rsaInitTimer) { clearTimeout(this.rsaInitTimer); this.rsaInitTimer = null; }
      }
    },

    /**
     * RSA加密密码
     */
    async rsaEncrypt(password) {
      try {
        if (!this.rsaPublicKey) {
          throw new Error('公钥未初始化');
        }

        // this.encryptStatus = '正在加密密码...';
        this.encryptStatusType = 'info';
        
        const encryptedPassword = await RSAEncryptionUtils.encryptWithPublicKey(
          password, 
          this.rsaPublicKey
        );
        
        // this.encryptStatus = '密码加密完成';
        this.encryptStatusType = 'success';
        
        return encryptedPassword;
      } catch (error) {
        // this.encryptStatus = '加密失败: ' + error.message;
        this.encryptStatusType = 'error';
        throw error;
      }
    },

    /**
     * 统一处理登录/注册前的密钥获取和参数准备
     */
    async prepareRequestParams() {
      if (!this.validateInput()) {
        return null;
      }

      try {
        // 先获取密钥
        const keyLoaded = await this.loadRsaKey();
        if (!keyLoaded) {
          return null;
        }

        // 再加密密码
        const encryptedPassword = await this.rsaEncrypt(this.password);
        
        // 返回通用参数
        return {
          userCode: this.userCode,
          username: this.username,
          password: encryptedPassword,
          keyId: this.keyId
        };
      } catch (error) {
        // this.encryptStatus = '准备请求参数失败: ' + error.message;
        this.encryptStatusType = 'error';
        return null;
      }
    },

    /**
     * 注册主方法
     */
    async register() {
      if (this.registerLoading) return;
      
      this.registerLoading = true;
      
      try {
        // 准备注册参数（包含密钥获取）
        const registerParams = await this.prepareRequestParams();
        if (!registerParams) {
          this.registerLoading = false;
          return;
        }

        // 调用注册接口
        const res1 = await userRegister(registerParams);
        const res = res1.data;
        
        if (res.code === 200) {
          this.$message.success('注册成功！');
          this.handleRememberPassword();
          
          setTimeout(() => {
            this.$message.info('请使用注册的账号密码进行登录');
          }, 1000);
          
        } else {
          const errorMsg = res.message || '注册失败，请检查输入信息';
          this.$message.error(errorMsg);
        }
      } catch (error) {
        // ⚠️ 与登录保持一致：只走一条出口（toast），不再叠加常驻横幅
        this.$message.error(this.errorText(this.classifyError(error)));
      } finally {
        this.registerLoading = false;
      }
    },

    /**
     * 登录主方法
     */
    async login() {
      if (this.loading) return;

      this.loading = true;
      this.loginStatus = '';
      this.loginStatusType = 'info';

      // 清除之前的登录定时器
      this.clearLoginTimers();

      try {
        // 准备登录参数（包含密钥获取）
        const loginParams = await this.prepareRequestParams();
        if (!loginParams) {
          this.loading = false;
          return;
        }

        // 为 userLogin 调用设置超时控制器，8秒超时
        const abortController = new AbortController();

        // 3秒: 提示"登录中..."
        this.loginHintTimer = setTimeout(() => {
          this.loginStatus = '登录中...';
          this.loginStatusType = 'info';
        }, 3000);

        // 5秒: 提示"网络较慢，正在连接..."
        this.loginHintTimer2 = setTimeout(() => {
          this.loginStatus = '网络较慢，正在连接...';
          this.loginStatusType = 'warning';
        }, 5000);

        // 8秒超时
        this.loginTimeoutTimer = setTimeout(() => {
          abortController.abort();
        }, 8000);

        // 调用登录接口
        const res1 = await userLogin(loginParams, { signal: abortController.signal });
        const res = res1.data;

        // 请求成功，清除登录状态提示
        this.loginStatus = '';
        this.deployHint = '';   // ✅ 能正常拿到响应，说明服务和资源都还在

        if (res.code === 200) {

          const { token, isManagement, position, department, urlPermission } = res.data;

          // 存储用户信息到 localStorage
          window.localStorage.setItem('token', token);
          window.localStorage.setItem('userCode', this.userCode);
          window.localStorage.setItem('username', this.username);
          window.localStorage.setItem('department', department);
          window.localStorage.setItem('position', position);
          window.localStorage.setItem('isAdmin', isManagement === '是' ? '1' : '0');
          // urlPermission：模块编码串（1项目 2合同 3生产 4产品 5部件），如 '2'、'2345'；管理员不受此字段限制
          window.localStorage.setItem('urlPermission', urlPermission || '');

          // 存储各页面列偏好（登录响应中携带）
          const prefPages = ['productProgress', 'workOrderProgress', 'contractProgress', 'projectProgress', 'abnormalProgress', 'partsProgress',
            'orderProgress',
            'productProgress_m', 'contractProgress_m', 'workOrderProgress_m', 'projectProgress_m', 'abnormalProgress_m', 'partsProgress_m',
            'orderProgress_m'];
          prefPages.forEach(key => {
            const val = res.data[key];
            if (val !== undefined && val !== null) {
              window.localStorage.setItem('columnPref_' + key, typeof val === 'string' ? val : JSON.stringify(val));
            }
          });


          this.handleRememberPassword();
          this.$message.success('登录成功');

          setTimeout(() => {
            // 默认进入可查看的最大模块主页（管理员→项目；如 '2345'→合同；无权→空菜单框架页）
            this.$router.push(UserInfoUtils.getDefaultHomeRoute());
          }, 500);

        } else {
          // 后端返回业务错误码
          const errorMsg = res.message || '登录失败，请检查用户名或密码';
          this.$message.error(errorMsg);
        }
      } catch (error) {
        const type = this.classifyError(error);

        if (type === 'http') {
          // 服务端返回了 HTTP 错误状态码（4xx 业务错误）
          const status = error.response.status;
          if (status === 401) {
            this.$message.error('用户名或密码错误');
          } else if (status === 403) {
            this.$message.error('没有访问权限，请联系管理员');
          } else {
            const msg = error.response.data && error.response.data.message;
            this.$message.error(msg || '登录请求失败');
          }
        } else {
          // ✅ 部署中 / 断网 / 连不上服务器 / 超时 / 5xx
          // ⚠️ 只走「登录阶段常驻 alert」这一条出口，不再叠加 $message 和 deployHint
          const text = this.errorText(type);
          this.loginStatus = text;
          this.loginStatusType = 'error';
          this.deployHint = '';
        }
      } finally {
        this.loading = false;
        this.clearLoginTimers();
      }
    },

    /**
     * 处理记住密码逻辑
     */
    handleRememberPassword() {
      if (this.savePassword) {
        Cookies.set('userCode', this.userCode, { expires: 30 });
        Cookies.set('userName', this.username, { expires: 30 });
        Cookies.set(
          'userPwd',
          CryptoJS.AES.encrypt(this.password, 'foodparty'),
          { expires: 30 }
        );
      } else {
        Cookies.remove('userCode');
        Cookies.remove('userName');
        Cookies.remove('userPwd');
      }
    },

    /**
     * 综合验证输入
     */
    validateInput() {
      if (!this.validateUserCode()) return false;
      if (!this.validateUsername()) return false;
      if (!this.validatePassword()) return false;
      
      return true;
    },
    
    /**
     * 验证工号
     */
    validateUserCode() {
      if (!this.userCode || this.userCode.trim() === '') {
        this.$message.warning('请输入工号');
        return false;
      }
      
      if (this.userCode.trim().length < 1) {
        this.$message.warning('工号格式不正确');
        return false;
      }
      
      return true;
    },

    /**
     * 验证用户名
     */
    validateUsername() {
      if (!this.username || this.username.trim() === '') {
        this.$message.warning('请输入用户名');
        return false;
      }
      
      return true;
    },

    /**
     * 验证密码
     */
    validatePassword() {
      if (!this.password || this.password.trim() === '') {
        this.$message.warning('请输入密码');
        return false;
      }
      
      return true;
    }
  }
};
</script>

<style scoped lang="scss">
/* 样式保持不变 */
#login {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(to right, rgb(29, 194, 213), rgb(40, 207, 190), rgb(149, 225, 159));
}

.main-box {
  width: 530px;
  height: 450px;
  background: rgb(255, 255, 255);
  box-shadow: 2px 2px 4px 4px rgba(196, 196, 196, 0.25);
  border-radius: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  padding: 20px 0;
}

.title {
  color: rgba(80, 141, 252, 0.6);
  font-family: Noto Sans SC;
  font-size: 24px;
  font-weight: 400;
  line-height: 36px;
  letter-spacing: 0px;
  text-align: center;
}

.user-code,
.username,
.password {
  width: 250px;
}

.encrypt-status,
.login-status {
  width: 250px;
  margin: 10px 0;
}

.bottom {
  display: flex;
  justify-content: space-around;
  align-items: center;
  width: 250px;
  margin-top: 10px;
}

@media (max-height: 600px) {
  .main-box {
    height: auto;
    min-height: 400px;
    justify-content: center;
    gap: 15px;
  }
}
</style>